/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.17.0
 */

import { FC, Fragment, useCallback, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock, PageTitle, SideMenu, SideMenuItem } from '../../../components/common';
import { ISideMenuItem } from '../../../components/common/SideMenu/SideMenuItem';
import {
    makeSelectPageAutoReportIdTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation, IPageAutoReportIdTranslation } from '../../../modules/internationalization/types';
import { Loader } from '../../../modules/loader/components/Loader';
import { IRootReducer } from '../../../reducers/rootReducer';
import { CLIENT_ID, IRouteParams, RoutePath } from '../../../routes/constants/routesConstants';
import { icons as themeIcons } from '../../../theme';
import ErrorPage from '../../ErrorPage/ErrorPage';
import { autoReportIdGetMetaStartAction, autoReportIdGetTabMetaStartAction } from '../actions';
import { IAutoReportIdReducer } from '../reducer';
import {
    makeSelectorAutoReportCurrentId,
    makeSelectorAutoReportIdIsError,
    makeSelectorAutoReportIdIsLoading,
    makeSelectorAutoReportIdName,
    makeSelectorAutoReportIdType,
} from '../selectors';
import {
    EPageAutoReportIdInfo,
    EPageAutoReportIdType,
    IAutoReportIdGetTabMetaStartPayload,
    TAutoReportIdPayload,
} from '../types';
import { PageAutoReportIdStyled } from './styled/PageAutoReportIdStyled';

interface IPageAutoReportIdTab extends Pick<ISideMenuItem, 'id' | 'title' | 'dataName'> {
    id: EPageAutoReportIdInfo;
    url: string;
}

const PageAutoReportId: FC<IConnectedProps & IDispatchProps> = ({
    id,
    isLoading,
    isError,
    children,
    getMeta,
    name,
    translationAutoReportId,
    translationCommon,
    type,
}) => {
    const { clientId } = useParams<IRouteParams>();
    const location = useLocation();

    const tabs: IPageAutoReportIdTab[] = useMemo(
        () => [
            {
                id: EPageAutoReportIdInfo.FormatDelivery,
                title: translationAutoReportId.sideMenu.formatDelivery,
                url: RoutePath.autoReportIdFormatDelivery.replace(CLIENT_ID, clientId),
                dataName: 'page-auto-report-side-menu-item-format-delivery',
            },
            {
                id: EPageAutoReportIdInfo.ScheduleParameters,
                title: translationAutoReportId.sideMenu.scheduleParameters,
                url: RoutePath.autoReportIdScheduleParameters.replace(CLIENT_ID, clientId),
                dataName: 'page-auto-report-side-menu-item-schedule-parameters',
            },
            ...(type === EPageAutoReportIdType.Subscriber
                ? [
                      {
                          id: EPageAutoReportIdInfo.Subscribers,
                          title: translationAutoReportId.sideMenu.subscribers,
                          url: RoutePath.autoReportIdSubscribers.replace(CLIENT_ID, clientId),
                          dataName: 'page-auto-report-side-menu-item-subscribers',
                      },
                  ]
                : []),
            ...(type === EPageAutoReportIdType.Account
                ? [
                      {
                          id: EPageAutoReportIdInfo.Accounts,
                          title: translationAutoReportId.sideMenu.accounts,
                          url: RoutePath.autoReportIdAccounts.replace(CLIENT_ID, clientId),
                          dataName: 'page-auto-report-side-menu-item-accounts',
                      },
                  ]
                : []),
        ],
        [clientId, type, translationAutoReportId],
    );

    useEffect(() => {
        if (clientId) {
            getMeta({ id: clientId });
        }
    }, [clientId, getMeta]);

    const renderSideMenuItem = useCallback(
        (item: IPageAutoReportIdTab) => (
            <LinkBlock key={item.id} url={item.url}>
                <SideMenuItem
                    dataName={item.dataName}
                    id={item.id}
                    isActive={item.url === location.pathname}
                    level={0}
                    title={item.title}
                />
            </LinkBlock>
        ),
        [location.pathname],
    );

    return (
        <Fragment>
            <PageTitle dataName="page-auto-report-id" title={name} />

            <PageAutoReportIdStyled>
                <SideMenu width={285}>{tabs.map(renderSideMenuItem)}</SideMenu>

                <div className="subscriber__content">
                    {isError ? (
                        <ErrorPage
                            src={themeIcons.errorDeliveryImage3x}
                            text={translationCommon.error.ui.text}
                            title={translationCommon.error.ui.title}
                        />
                    ) : (
                        <Loader showLoader={isLoading}>{id && clientId === id && children}</Loader>
                    )}
                </div>
            </PageAutoReportIdStyled>
        </Fragment>
    );
};

interface IConnectedProps extends Pick<IAutoReportIdReducer, 'id' | 'isLoading' | 'isError' | 'name' | 'type'> {
    translationAutoReportId: IPageAutoReportIdTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    id: makeSelectorAutoReportCurrentId(),
    isError: makeSelectorAutoReportIdIsError(),
    isLoading: makeSelectorAutoReportIdIsLoading(),
    name: makeSelectorAutoReportIdName(),
    type: makeSelectorAutoReportIdType(),
    translationAutoReportId: makeSelectPageAutoReportIdTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    getMeta: (payload: TAutoReportIdPayload) => void;
    getTabMeta: (payload: IAutoReportIdGetTabMetaStartPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getMeta: (payload) => dispatch(autoReportIdGetMetaStartAction(payload)),
    getTabMeta: (payload) => dispatch(autoReportIdGetTabMetaStartAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PageAutoReportId);
